import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import styles from './AgentCard.scss';
import getAgentURL from '../../lib/urlWriters/agentURL';
import getAgencyURL from '../../lib/urlWriters/agencyURL';
import {dataLayerPush, trackBeginCheckout} from '../../lib/tracking';
import ClickToContactAgent from '../AgencyCards/ClickToContactAgent';
import Linkify from '../Linkify/Linkify';
import AgentAvatar from '../AgentAvatar/AgentAvatar';
import {getRmaHeadshot, getRmaLogo} from '../../lib/rmaUtils';
import {getAuctionSaleType} from '../../selectors/openn';

function AgentCard(props) {
  const {agency} = props;
  const propertyDetail = useSelector(state => state.propertyDetail);
  const hasESDetails = !props.isRecentSold || agency?.agencyId;
  const agentList = agency?.agents && agency.agents.length > 2 ? agency.agents.slice(0, 2) : agency.agents;
  const agencyUrl = hasESDetails ? getAgencyURL(agency) : null;

  const gtmClass = props.category === 'SaleListing' ? 'gtm-listing-enquiry-for-sale' : 'gtm-listing-enquiry-for-rent';

  const rmaAgencyProfile = useSelector(state => state.rmaAgencyProfiles);
  const agencyLogo = agency?.logo ? agency.logo : getRmaLogo(rmaAgencyProfile, agency.agencyId);

  const rmaAgentProfiles = useSelector(state => state.rmaAgentProfiles);
  const opennAuction = useSelector(state => state.opennAuction);

  let bgColor = '#d7d9db';
  if (agency?.bannerHex) bgColor = `${agency.bannerHex.indexOf('#') === 0 ? '' : '#'}${agency.bannerHex}`;

  let othSignUpUrl = '/list-on-onthehouse?utm_source=oth&utm_medium=website&utm_campaign=sold_pdp';

  useEffect(() => {
    othSignUpUrl = `${process.env.ODIN_BASE_URL}${othSignUpUrl}`;
  }, []);

  const scrollToForm = (event) => {
    event.preventDefault();
    if (typeof window === 'object') {
      let form = document.getElementById('listing-enquiry-form');
      let rect = form.getBoundingClientRect();
      let top = Math.floor(rect.top);
      window.scrollTo(0, top);
    }
  };

  const trackClick = () => {
    dataLayerPush({event: gtmClass});
    if (propertyDetail && propertyDetail.status === 'success') {
      trackBeginCheckout(propertyDetail.property,null,null,null, getAuctionSaleType(opennAuction));
    }
  };

  const handleMessageAgentLinkClick = (event) => {
    trackClick();
    scrollToForm(event);
  };

  const getCreateProfileView = (isAgentNameDisclosed) => {
    return <div style={{fontSize: 'small', paddingTop: '5px'}}>
      {isAgentNameDisclosed ? 'Work here?' : 'Is this you?'} <a href={othSignUpUrl} target="_blank" rel="nofollow noopener noreferrer">Create your free agency profile</a>
    </div>;
  };

  const getAgentContactInfo = (agent) => {
    let agentContact;

    if (agent?.contact) {
      agentContact = agent.contact;
    } else {
      agentContact = {email: agency.email, phone: { business: agency.phoneNumber }};
    }

    return <div className={'d-flex align-items-baseline'}>
      {
          agentContact?.email &&
          <div className={'mr-4'}>
            <a
              className={styles.enquireButton + ' ' + gtmClass}
              onClick={handleMessageAgentLinkClick}
              href="#listing-enquiry-form"
            >
              Message
            </a>
          </div>
      }
      {
          agentContact?.phone &&
          <ClickToContactAgent
            agencyID={agency?.agencyId}
            agent={agent?.contact ? agent : null}
            contact={agentContact}
            trackEvent="clickToContact-AgentCard"
            context="pdp"
          />
      }
    </div>;
  };

  const getAgentView = (agent, index) => {
    const agentUrl = hasESDetails && agent?.uuid && agent?.name ? getAgentURL(agent, agency) : null;
    const agentHeadShot = agent?.headShot ? agent.headShot : getRmaHeadshot(rmaAgentProfiles, agent?.uuid, '/assets/images/profile_icon.svg');
    const isNameDisclosed = agent?.name?.trim() ? false : true;
    const hasContactInformation = agent?.contact?.email || agent?.contact?.phone;

    return (
      <React.Fragment>
        { index !== 0 && <div className={styles.agentSeparator}></div>}
        <div className={`d-flex align-items-center ${(agentList.length === 2 && index === 0) && 'mb-3'}`} key={agent?.uuid || agency?.name?.trim()}>
          <div>
            <Linkify
              content={<AgentAvatar size="xl" avatarUrl={agentHeadShot} />}
              url={agentUrl}
            />
          </div>

          <div className={styles.rightPanel + ' flex-grow'}>
            {!isNameDisclosed &&
            <Linkify
              content={agent?.name.trim()}
              linkClass={'lgText bold600 ' + styles.agentName}
              url={agentUrl}
            />}
            <Linkify
              content={agency?.name?.trim() || 'Agency name not disclosed'}
              linkClass={styles.agencyName}
              url={agencyUrl}
            />

            {(isNameDisclosed || !hasContactInformation) && !agencyUrl ? getCreateProfileView(isNameDisclosed) : getAgentContactInfo(agent)}

            {index === agentList.length - 1 || agentList.length === 0 && !agencyLogo &&
              <Linkify
                content="See more from this agency"
                containerClass="my-2"
                linkClass={styles.seeMoreFromAgencyLink}
                url={agencyUrl}
                isHidden={!agencyUrl}
              />
            }

          </div>
        </div>
      </React.Fragment>
    );
  };

  return (
    <div id="listing-agent-card" className="mb-3 w-100">
      {props?.isRecentSold && <div className="bold600 mb-2">Sold By</div>}
      {!props?.isRecentSold && <div className="bold600 mb-2">Contact Agent{agentList?.length > 1 && 's'}</div>}
      <div className={`d-flex flex-column ${styles.agentDetails}`} style={{ borderTopColor: bgColor }}>
        {agentList?.length > 1 ? agentList.map((agent, index) => getAgentView(agent, index)) : getAgentView(agentList[0], 0)}

        { agencyLogo &&
          <div className={'d-flex align-items-baseline'}>
            <Linkify
              content={<img className={styles.agencyLogo} src={agencyLogo} />}
              containerClass={`text-center mt-2 ${styles.agencyLogoContainer}` }
              url={agencyUrl}
            />
            <Linkify
              content="See more from this agency"
              containerClass="my-2"
              linkClass={styles.seeMoreFromAgencyLink + ' ' + styles.seeMoreFromAgencyLinkWithLogo}
              url={agencyUrl}
              isHidden={!agencyUrl}
            />
          </div>
        }

      </div>
    </div>
  );
}

AgentCard.propTypes = {
  agency: PropTypes.object.isRequired,
  category: PropTypes.string.isRequired,
  isRecentSold: PropTypes.bool,
};

export default AgentCard;
